import {
  getApps,
  initializeApp,
  type FirebaseApp,
  type FirebaseOptions,
} from 'firebase/app';

export interface FirebaseEnvironment {
  readonly [key: string]: string | boolean | undefined;
  VITE_FIREBASE_API_KEY?: string;
  VITE_FIREBASE_DATABASE_URL?: string;
  VITE_FIREBASE_PROJECT_ID?: string;
  VITE_FIREBASE_STORAGE_BUCKET?: string;
  VITE_ADMIN_API_BASE_URL?: string;
}

export type FirebaseConfigurationResult =
  | {
      config: FirebaseOptions;
      adminApiBaseUrl: string;
    }
  | {
      config?: never;
      adminApiBaseUrl?: never;
      error: string;
      missingKeys: readonly FirebaseRequiredEnvironmentKey[];
    };

/**
 * The Admin uses Firebase for Realtime Database reads, Storage reads, and the
 * custom-token session only. Messaging, Analytics and Installations are not
 * used, so their keys are deliberately absent.
 */
export const requiredEnvironmentKeys = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_DATABASE_URL',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_ADMIN_API_BASE_URL',
] as const;

export type FirebaseRequiredEnvironmentKey =
  (typeof requiredEnvironmentKeys)[number];

export function resolveFirebaseConfiguration(
  environment: FirebaseEnvironment,
): FirebaseConfigurationResult {
  const missingKeys = requiredEnvironmentKeys.filter(
    (key) => !environment[key]?.toString().trim(),
  );

  if (missingKeys.length > 0) {
    return {
      error: `Missing Admin environment variables: ${missingKeys.join(', ')}`,
      missingKeys,
    };
  }

  const config: FirebaseOptions = {
    apiKey: environment.VITE_FIREBASE_API_KEY?.trim(),
    databaseURL: environment.VITE_FIREBASE_DATABASE_URL?.trim(),
    projectId: environment.VITE_FIREBASE_PROJECT_ID?.trim(),
    storageBucket: environment.VITE_FIREBASE_STORAGE_BUCKET?.trim(),
  };

  return {
    config,
    adminApiBaseUrl: (environment.VITE_ADMIN_API_BASE_URL ?? '')
      .toString()
      .trim()
      .replace(/\/+$/, ''),
  };
}

/**
 * Initialises the Firebase app once, here rather than through
 * `@firecms/firebase`'s hook.
 *
 * Two reasons. Firebase initialisation belongs in this module by convention,
 * and — measurably — routing it through `@firecms/firebase` would drag the
 * whole FireCMS runtime onto the sign-in path, which needs only `firebase/app`
 * and `firebase/auth` to exchange the custom token.
 */
export function initialiseAdminFirebaseApp(
  config: FirebaseOptions,
): FirebaseApp {
  const [existing] = getApps();
  return existing ?? initializeApp(config);
}
